import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { HistoryLink } from '../Library/ReleaseHistory';
import { libraryReleaseClient } from '../Library/reuseClient';
const ids = Testids.customerContext;
const statusText = {
    current: 'Current reviewed version',
    source_changed: 'Source changed — review needed',
    source_unavailable: 'Source unavailable — review needed',
    withdrawn: 'Withdrawn — retained history',
    superseded: 'Superseded — retained history'
};
export function IncomingCustomerContext({ customerOrgId = '' }) {
    const generation = useRef(0);
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [refresh, setRefresh] = useState(0);
    useEffect(()=>{
        const focus = ()=>{
            generation.current++;
            setRows([]);
            setSelected(undefined);
            setRefresh((n)=>n + 1);
        };
        window.addEventListener('focus', focus);
        return ()=>window.removeEventListener('focus', focus);
    }, []);
    useEffect(()=>{
        const controller = new AbortController();
        setRows([]);
        setSelected(undefined);
        setLoading(true);
        setError(false);
        void libraryReleaseClient.listCustomerContexts({
            customerOrgId
        }, {
            signal: controller.signal
        }).then((result)=>{
            if (!controller.signal.aborted) setRows(result.summaries);
        }).catch(()=>{
            if (!controller.signal.aborted) setError(true);
        }).finally(()=>{
            if (!controller.signal.aborted) setLoading(false);
        });
        return ()=>{
            generation.current++;
            controller.abort();
        };
    }, [
        customerOrgId,
        refresh
    ]);
    const open = async (row)=>{
        const current = ++generation.current;
        setSelected(undefined);
        setError(false);
        try {
            const result = await libraryReleaseClient.getCustomerContext({
                libraryItemId: row.libraryItemId,
                releaseId: row.releaseId
            });
            if (current === generation.current) setSelected(result.summary);
        } catch  {
            if (current === generation.current) {
                setRows([]);
                setError(true);
            }
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": ids.incoming,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Incoming shared customer context"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        children: "Summaries shared with you. Open one to read it and see its history."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>{
                            generation.current++;
                            setRows([]);
                            setSelected(undefined);
                            setRefresh((n)=>n + 1);
                        },
                        children: "Refresh shared context"
                    }),
                    loading && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        "aria-live": "polite",
                        children: "Loading shared context…"
                    }),
                    error && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        children: "Shared context is unavailable. Refresh to check your access."
                    }),
                    !loading && !error && rows.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Nothing shared with you yet."
                    }),
                    rows.map((row)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                            variant: "secondary",
                            "data-testid": ids.open,
                            onClick: ()=>void open(row),
                            children: [
                                row.title,
                                " · v",
                                row.version,
                                " · ",
                                statusText[row.status] || 'Review needed'
                            ]
                        }, row.releaseId)),
                    selected && /*#__PURE__*/ _jsx(SpecraCard, {
                        "data-testid": ids.derivative,
                        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "font-medium",
                                    children: selected.title
                                }),
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: selected.status === 'current' ? 'success' : 'warning',
                                    children: statusText[selected.status] || 'Review needed'
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "whitespace-pre-wrap",
                                    children: selected.summary
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    size: "meta",
                                    children: [
                                        selected.provenance,
                                        " · reviewed",
                                        ' ',
                                        selected.reviewedAt?.toDate().toLocaleDateString() || 'Not recorded',
                                        " · version",
                                        ' ',
                                        selected.version
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    size: "meta",
                                    children: [
                                        "Shared ",
                                        selected.releasedAt?.toDate().toLocaleString() || 'date not recorded'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(HistoryLink, {
                                    itemId: selected.libraryItemId
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    onClick: ()=>setSelected(undefined),
                                    children: "Close summary"
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
